import * as fs from "node:fs";
import * as path from "node:path";
import { LocalCliError } from "./errors.js";
import { classifyCandidate, type Candidate } from "./seed-merge.js";
import { composeSeedPrBody, composeSeedPrTitle } from "./seed-pr-body.js";
import {
  SEED_MANIFEST_PATH,
  upsertLineage,
  serializeSeedManifest,
  deriveOwnedPaths,
  type SeedLineage,
  type SeedManifest,
} from "./seed-manifest.js";
import type { FileChange, OpenPr } from "./github-app-repo.js";

export interface SeedFileSource { readOurs(repoRelPath: string): Promise<string | null> }
export interface SeedTrees { baseDir: string | null; theirsDir: string; ownedAtTheirs: string[] }

function readIf(dir: string | null, rel: string): string | null {
  if (!dir) return null;
  const abs = path.join(dir, rel);
  return fs.existsSync(abs) ? fs.readFileSync(abs, "utf8") : null;
}

/** Union base-owned + theirs-owned paths; read base/theirs from local dirs, ours from the source. */
export async function assembleCandidates(args: {
  lineage: SeedLineage | null; trees: SeedTrees; source: SeedFileSource; seedName: string;
}): Promise<Candidate[]> {
  const paths = new Set<string>([...(args.lineage?.ownedPaths ?? []), ...args.trees.ownedAtTheirs]);
  const out: Candidate[] = [];
  for (const p of [...paths].sort((a, b) => a.localeCompare(b))) {
    out.push({ path: p, base: readIf(args.trees.baseDir, p), theirs: readIf(args.trees.theirsDir, p), ours: await args.source.readOurs(p) });
  }
  return out;
}

/** Any skills/<slug>/SKILL.md a merged index references must exist or be in the PR's final set. */
export function assertNoDanglingIndexRefs(finalPaths: Set<string>, repoHas: (p: string) => boolean, indexBodies: { path: string; body: string }[]): void {
  const refRe = /skills\/[^)\s]+\/SKILL\.md/g;
  for (const idx of indexBodies) {
    for (const m of idx.body.matchAll(refRe)) {
      const ref = m[0];
      if (!finalPaths.has(ref) && !repoHas(ref)) {
        throw new LocalCliError({
          code: "PLATFORM_SEED_INDEX_DANGLING",
          message: `Refusing to open a PR: ${idx.path} would reference a missing skill '${ref}'. The brain loader only opens indexed paths.`,
        });
      }
    }
  }
}

/** The injected App surface `refreshBrainRepo` operates against — thin wrappers so
 *  tests inject a fake; production binds these to `github-app-repo.ts`. */
export interface RepoRefreshApi {
  getDefaultBranch(): Promise<string>;
  getHeadSha(branch: string): Promise<string>;
  findOpenPr(head: string): Promise<{ number: number; htmlUrl: string } | null>;
  /** Commit `files` as ONE commit parented on `baseSha` and point `branch` at it (create or
   *  force-update) in a single ref update. Rebuilds the PR branch cleanly from base on every
   *  run WITHOUT ever transiting an empty (branch==base) state — a two-step reset-then-commit
   *  transiently empties the PR diff, which GitHub auto-closes the PR on. */
  commitFromBase(files: FileChange[], branch: string, baseSha: string, message: string): Promise<void>;
  openPr(head: string, base: string, title: string, body: string): Promise<{ number: number; htmlUrl: string }>;
  updatePr(n: number, title: string, body: string): Promise<void>;
  closePr(n: number, comment: string): Promise<void>;
  readFileAtRef(path: string, ref: string): Promise<string | null>;
}

export interface RepoRefreshResult {
  repo: string;
  prUrl: string | null;
  action: "opened" | "updated" | "noop" | "bootstrapped";
  updated: string[];
  added: string[];
  deleted: string[];
  skipped: { path: string; skipReason: string }[];
}

/**
 * Compute merge outcomes for one brain repo's seed lineage, build the PR file set
 * (the `.m8t/seed-manifest.json` marker bump rides IN the PR), and open/update/
 * supersede the PR via the injected App API. Only opens a PR when content actually
 * changed; the marker alone is never sufficient to open one.
 */
export async function refreshBrainRepo(args: {
  repo: string;
  branch: string;
  base: string;
  version: string;
  notesUrl: string | null;
  /** Founder-facing display name for the PR copy (e.g. "Azure Advisor"). Display only. */
  advisorLabel: string;
  /** The seed LINEAGE name (e.g. "azure-advisor") — the key the marker records and the
   *  manifest's `brainSeeds.items` is keyed by. MUST be the seed name, never the display label,
   *  or a re-read marker won't match the manifest and the repo would be skipped forever. */
  seedName: string;
  lineage: SeedLineage | null;
  trees: SeedTrees;
  source: SeedFileSource;
  api: RepoRefreshApi;
  targetCommit?: string;
  supersedeBranches?: string[];
  priorManifest?: SeedManifest | null;
}): Promise<RepoRefreshResult> {
  const cands = await assembleCandidates({ lineage: args.lineage, trees: args.trees, source: args.source, seedName: args.seedName });
  const outcomes = cands.map(classifyCandidate);

  const files: FileChange[] = [];
  const updated: string[] = [];
  const added: string[] = [];
  const deleted: string[] = [];
  const skipped: { path: string; skipReason: string }[] = [];
  const indexBodies: { path: string; body: string }[] = [];
  const finalPaths = new Set<string>();

  for (const o of outcomes) {
    if (o.path === SEED_MANIFEST_PATH) continue; // the marker is (re)written below, from the target rev
    if (o.kind === "update") {
      files.push({ path: o.path, content: o.content ?? "" });
      updated.push(o.path);
      finalPaths.add(o.path);
    } else if (o.kind === "add") {
      files.push({ path: o.path, content: o.content ?? "" });
      added.push(o.path);
      finalPaths.add(o.path);
    } else if (o.kind === "delete") {
      files.push({ path: o.path, delete: true });
      deleted.push(o.path);
    } else if (o.kind === "skip") {
      skipped.push({ path: o.path, skipReason: o.skipReason ?? "left as-is" });
    }
    if (o.path.endsWith("/_index.md") && (o.kind === "update" || o.kind === "add")) {
      indexBodies.push({ path: o.path, body: o.content ?? "" });
    }
  }

  const contentChanged = updated.length + added.length + deleted.length > 0;
  if (!contentChanged) {
    return { repo: args.repo, prUrl: null, action: "noop", updated, added, deleted, skipped };
  }

  // Index-consistency: no merged index may reference a skill that neither exists nor is in this PR.
  const ownedNow = new Set(args.trees.ownedAtTheirs);
  assertNoDanglingIndexRefs(finalPaths, (p) => ownedNow.has(p), indexBodies);

  // Bump the marker IN this PR (the marker travels with the delivery, not a separate PR).
  const newOwned = deriveOwnedPaths(args.trees.theirsDir);
  const nextManifest = upsertLineage(
    args.priorManifest ?? (args.lineage ? { schemaVersion: 1, seeds: [args.lineage] } : null),
    {
      name: args.seedName,
      refreshedToCommit: args.targetCommit ?? "",
      refreshedToVersion: args.version,
      ownedPaths: newOwned,
    },
  );
  files.push({ path: SEED_MANIFEST_PATH, content: serializeSeedManifest(nextManifest) });

  // Supersede older open seed PRs on other branches (never the branch we're about to use).
  for (const sb of args.supersedeBranches ?? []) {
    if (sb === args.branch) continue;
    const stale = await args.api.findOpenPr(sb);
    if (stale) await args.api.closePr(stale.number, `superseded by the newer update on \`${args.branch}\`.`);
  }

  const opened = await args.api.findOpenPr(args.branch);

  const headSha = await args.api.getHeadSha(args.base);
  await args.api.commitFromBase(files, args.branch, headSha, `chore(seed): refresh built-in skills & guides (${args.version})`);

  const summary = {
    advisorLabel: args.advisorLabel,
    version: args.version,
    notesUrl: args.notesUrl,
    repo: args.repo,
    updated,
    added,
    deleted,
    skipped,
  };
  const title = composeSeedPrTitle(summary);
  const body = composeSeedPrBody(summary);

  if (opened) {
    await args.api.updatePr(opened.number, title, body);
    return { repo: args.repo, prUrl: opened.htmlUrl, action: "updated", updated, added, deleted, skipped };
  }
  const pr: OpenPr = await args.api.openPr(args.branch, args.base, title, body);
  return { repo: args.repo, prUrl: pr.htmlUrl, action: args.lineage ? "opened" : "bootstrapped", updated, added, deleted, skipped };
}
